import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import { isSafeExternalUrl, type OnlineOrderRow } from '@his/shared';
import { get, post, put, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { tr } from '@/lib/i18n-core';
import { visibleInterval } from '@/lib/polling';
import { dateTime, money } from '@/lib/utils';
import { DataTable, Dialog, PageHeader, Tabs } from '@/components/ui/layout';
import { Alert, Badge, Button, Field, StatusBadge, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

/**
 * Medicine orders placed from the public directory.
 *
 * Approval means two separate things at once and the screen keeps them
 * visible: the money has arrived (or is coming on delivery), and the order is
 * one the pharmacy is willing to dispense. A transfer shows its slip right
 * here so the person approving is looking at the evidence, not at a checkbox.
 */
export function OnlineOrdersPage() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState('pending');
  const [viewing, setViewing] = useState<OnlineOrderRow | null>(null);
  const [rejecting, setRejecting] = useState<OnlineOrderRow | null>(null);

  const manage = can('pharmacy.pos');

  const orders = useQuery({
    queryKey: ['online-orders', status],
    queryFn: () => get<OnlineOrderRow[]>(`/directory/orders${qs({ status: status || undefined })}`),
    refetchInterval: visibleInterval(120_000),
  });

  const openProof = useMutation({
    mutationFn: (id: string) => post<{ url: string }>(`/directory/orders/${id}/payment-proof-link`),
    onSuccess: ({ url }) => {
      window.open(url, '_blank', 'noopener');
    },
    onError: (error) => toast.error(tr('pharmacy', 'could_not_open_the_transfer_slip'), describeError(error)),
  });

  const act = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'approve' | 'dispensed' }) =>
      put(`/directory/orders/${id}/${action}`),
    onSuccess: (_result, variables) => {
      toast.success(variables.action === 'approve' ? 'Order approved' : 'Marked as handed over');
      void queryClient.invalidateQueries({ queryKey: ['online-orders'] });
      setViewing(null);
    },
    onError: (error) => toast.error('Could not update the order', describeError(error)),
  });

  const pending = orders.data?.filter((row) => row.status === 'pending').length ?? 0;

  return (
    <>
      <PageHeader title="Online orders" description="Medicine orders placed from the public directory" />

      <Tabs
        tabs={[
          {
            id: 'pending',
            label: 'Waiting',
            badge: status === 'pending' && pending > 0 ? <Badge variant="warning">{pending}</Badge> : undefined,
          },
          { id: 'approved', label: 'Approved' },
          { id: 'dispensed', label: 'Handed over' },
          { id: '', label: 'All' },
        ]}
        active={status}
        onChange={setStatus}
      />

      <DataTable
        rows={orders.data}
        loading={orders.isLoading} refreshing={orders.isFetching}
        error={orders.error}
        rowKey={(row) => row.id}
        empty="No online orders yet."
        columns={[
          { header: 'Order', sort: (row) => row.number, cell: (row) => <span className="tabular font-medium">{row.number}</span> },
          {
            header: 'Customer',
            sort: (row) => row.customerName,
            cell: (row) => (
              <div>
                <p className="font-medium">{row.customerName}</p>
                <p className="text-xs text-muted-foreground">{row.phone}</p>
              </div>
            ),
          },
          { header: 'Items', align: 'right', sort: (row) => row.items.length, cell: (row) => row.items.length },
          { header: 'Total', align: 'right', sort: (row) => row.totalMinor, cell: (row) => <span className="tabular">{money(row.totalMinor)}</span> },
          {
            header: 'Payment',
            sort: (row) => row.paymentMethod,
            cell: (row) => (
              <Badge variant={row.paymentMethod === 'cod' ? 'muted' : 'secondary'}>
                {row.paymentMethod === 'cod' ? 'cash on delivery' : 'transfer'}
              </Badge>
            ),
          },
          { header: 'Placed', sort: (row) => row.createdAt, cell: (row) => <span className="text-sm">{dateTime(row.createdAt)}</span> },
          { header: 'Status', sort: (row) => row.status, cell: (row) => <StatusBadge status={row.status} /> },
          {
            header: '',
            align: 'right',
            cell: (row) => (
              <div className="flex justify-end gap-1">
                <Button size="sm" variant="outline" onClick={() => setViewing(row)}>
                  Open
                </Button>
                {manage && row.status === 'approved' && (
                  <Button
                    size="sm"
                    onClick={() => act.mutate({ id: row.id, action: 'dispensed' })}
                    loading={act.isPending}
                  >
                    Handed over
                  </Button>
                )}
              </div>
            ),
          },
        ]}
      />

      {viewing && (
        <Dialog
          open
          onClose={() => setViewing(null)}
          wide
          title={`Order ${viewing.number}`}
          description={`${viewing.customerName} · ${viewing.phone}`}
          footer={
            manage && viewing.status === 'pending' ? (
              <>
                <Button variant="ghost" onClick={() => setViewing(null)}>
                  Close
                </Button>
                <Button variant="outline" onClick={() => setRejecting(viewing)}>
                  Reject
                </Button>
                <Button
                  onClick={() => act.mutate({ id: viewing.id, action: 'approve' })}
                  loading={act.isPending}
                >
                  Payment verified — approve
                </Button>
              </>
            ) : (
              <Button variant="ghost" onClick={() => setViewing(null)}>
                Close
              </Button>
            )
          }
        >
          <div className="space-y-4">
            {viewing.status === 'rejected' && viewing.rejectionReason && (
              <Alert variant="destructive">Rejected: {viewing.rejectionReason}</Alert>
            )}

            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Deliver to
              </p>
              <p className="mt-1 text-sm">{viewing.address}</p>
            </div>

            <div className="rounded-lg border">
              <table className="w-full text-sm">
                <tbody>
                  {viewing.items.map((item) => (
                    <tr key={item.id} className="border-b last:border-0">
                      <td className="px-3 py-2">{item.name}</td>
                      <td className="px-3 py-2 text-end tabular">× {item.qty}</td>
                      <td className="px-3 py-2 text-end tabular">{money(item.lineTotalMinor)}</td>
                    </tr>
                  ))}
                  <tr className="bg-muted/50 font-medium">
                    <td className="px-3 py-2" colSpan={2}>
                      Total
                    </td>
                    <td className="px-3 py-2 text-end tabular">{money(viewing.totalMinor)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Payment</p>
              <p className="mt-1 text-sm">
                {viewing.paymentMethod === 'cod' ? 'Cash on delivery' : 'Bank or wallet transfer'}
                {viewing.paymentRef ? ` · reference ${viewing.paymentRef}` : ''}
              </p>
              {viewing.hasPaymentProofFile ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-1"
                  onClick={() => openProof.mutate(viewing.id)}
                  loading={openProof.isPending}
                >
                  <ExternalLink className="h-3.5 w-3.5" /> {tr('pharmacy', 'open_the_transfer_slip')}
                </Button>
              ) : (
                viewing.paymentProofUrl && (
                  <CustomerLink href={viewing.paymentProofUrl} label={tr('pharmacy', 'open_the_transfer_slip')} />
                )
              )}
            </div>

            {viewing.prescriptionUrl && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Prescription
                </p>
                <CustomerLink href={viewing.prescriptionUrl} label={tr('pharmacy', 'open_the_prescription')} />
              </div>
            )}

            {viewing.notes && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Notes</p>
                <p className="mt-1 text-sm">{viewing.notes}</p>
              </div>
            )}
          </div>
        </Dialog>
      )}

      {rejecting && (
        <RejectOrderDialog
          order={rejecting}
          onClose={() => setRejecting(null)}
          onDone={() => {
            setRejecting(null);
            setViewing(null);
            void queryClient.invalidateQueries({ queryKey: ['online-orders'] });
          }}
        />
      )}
    </>
  );
}

function CustomerLink({ href, label }: { href: string; label: string }) {
  if (!isSafeExternalUrl(href)) {
    return (
      <div className="mt-1 text-sm">
        <p className="text-xs text-muted-foreground">{tr('pharmacy', 'not_a_web_link_shown_as_text')}</p>
        <p className="break-all font-mono">{href}</p>
      </div>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className="mt-1 inline-flex items-center gap-1.5 rounded-md text-sm text-primary hover:underline focus-ring"
    >
      <ExternalLink className="h-3.5 w-3.5" /> {label}
    </a>
  );
}

function RejectOrderDialog({
  order,
  onClose,
  onDone,
}: {
  order: OnlineOrderRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reject = useMutation({
    mutationFn: () => put(`/directory/orders/${order.id}/reject`, { reason }),
    onSuccess: () => {
      toast.success('Order rejected', 'The customer can see the reason you gave.');
      onDone();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Reject order ${order.number}`}
      description="The customer sees this reason"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              setError(null);
              reject.mutate();
            }}
            loading={reject.isPending}
            disabled={reason.trim().length < 3}
          >
            Reject
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <Alert variant="destructive">{error}</Alert>}
        <Field label="Reason" required>
          <Textarea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="We could not verify the transfer — please send the correct slip."
          />
        </Field>
      </div>
    </Dialog>
  );
}
